import UploadStep     from './MassInvoiceUploadStep';
import MappingStep    from './MassInvoiceMappingStep';
import GridStep       from './MassInvoiceGridStep';
import PreviewStep    from './MassInvoicePreviewStep';
import React, { useState } from 'react';

export default function MassInvoiceWizard() {
  const [step, setStep] = useState<'upload' | 'mapping' | 'grid' | 'preview'>('upload');
  const [rows, setRows] = useState<any[]>([]);
  const [mapping, setMapping] = useState<any>({});
  const [sessionId] = useState<string>(() => crypto.randomUUID());
  const [mode, setMode] = useState<'upload' | 'new'>('upload');
  const [gridState, setGridState] = useState<any[] | undefined>(undefined);
  const [containsDates, setContainsDates] = useState<boolean>(true);
  const [monthStart, setMonthStart] = useState<string | null>(null);

  const createBlankGrid = () => {
    const today = new Date().toISOString().slice(0, 10);
    // Default mapping keys expected by the Grid
    const defaultMapping = {
      customer: 'customer',
      date: 'date',
      service: 'service',
      quantity: 'quantity',
      rate: 'rate'
    };
    // Seed with a single blank row including one date value
    const initialRows = [
      { customer: '', date: today, service: '', quantity: 1, rate: 0 }
    ];
    setMapping(defaultMapping);
    setRows(initialRows);
    setStep('grid');
  };

  const ensureMonthSelected = async (): Promise<boolean> => {
    if (containsDates) return true;
    if (monthStart) return true;
    const today = new Date();
    const defaultValue = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}`;
    const input = window.prompt('Select month for all services (YYYY-MM):', defaultValue);
    if (!input) return false;
    const m = input.match(/^(\d{4})-(\d{2})$/);
    if (!m) { alert('Invalid month. Use YYYY-MM.'); return false; }
    const y = Number(m[1]);
    const mm = Number(m[2]);
    if (mm < 1 || mm > 12) { alert('Month must be 01-12'); return false; }
    setMonthStart(`${y}-${String(mm).padStart(2,'0')}-01`);
    return true;
  };

  return (
	<main className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
		{step === 'upload' && (
			<>
				<div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:bg-neutral-900 dark:border-neutral-800 p-5 mb-4">
					<div className="flex gap-2">
						<button
							className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra ${mode==='upload' ? 'bg-sedra text-white hover:bg-sedra-700' : 'border border-sedra text-sedra hover:bg-sedra/5'}`}
							onClick={() => setMode('upload')}
						>Upload CSV</button>
						<button
							className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra ${mode==='new' ? 'bg-sedra text-white hover:bg-sedra-700' : 'border border-sedra text-sedra hover:bg-sedra/5'}`}
							onClick={() => setMode('new')}
						>Start New File</button>
					</div>
				</div>
				{mode === 'upload' ? (
					<UploadStep 
						onParsed={(data) => { setRows(data); setStep('mapping'); }}
						onModeChange={(flag)=> setContainsDates(flag)}
					/>
				) : (
					<div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:bg-neutral-900 dark:border-neutral-800 p-5">
						<p className="text-gray-600 mb-2">Start a new blank grid.</p>
						<button
							className="rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white hover:bg-sedra-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
							onClick={createBlankGrid}
						>Create Blank Grid</button>
					</div>
				)}
			</>
		)}
		{step === 'mapping' && (
			<MappingStep rows={rows} containsDates={containsDates} onDone={async (map) => { 
				setMapping(map); 
				// In no-dates mode, ensure month is selected before grid
				const ok = await ensureMonthSelected();
				if (!ok) return; 
				setStep('grid'); 
			}} />
		)}
		{step === 'grid' && (
			<GridStep
				sessionId={sessionId}
				rows={rows}
				mapping={mapping}
				initialSpreadsheetRows={gridState}
				onSpreadsheetRowsChange={(s) => setGridState(s)}
				onValidated={() => setStep('preview')}
				// new props
				containsDates={containsDates}
				monthStart={monthStart || undefined}
			/>
		)}
		{step === 'preview' && (
			<PreviewStep sessionId={sessionId} onDone={() => setStep('upload')} onBack={() => setStep('grid')} containsDates={containsDates} monthStart={monthStart || undefined} />
		)}
	</main>
);
} 